<template>
  <section id="chat" :class="{ sidebarOpen: sidebarOpen }">
    <transition name="fade">
      <div v-if="!init && chatEnabled" class="loading">
        <div class="v-align">
          <div class="inner">
            <Notification
              :message="visualSettings.initializingChatLabel || 'Initializing...'"
              :icon="['fas', 'spinner']" 
              type="info"
            />
          </div>
        </div>
      </div>
    </transition>
    <transition appear name="fade">
      <section v-if="init" class="sidebar" :style="{ backgroundColor: visualSettings.sidebarBackgroundColor }">
        <img class="logo" v-if="visualSettings.sidebarLogo" :src="visualSettings.sidebarLogo" />
        <p class="tagline" v-if="visualSettings.sidebarTagline">{{visualSettings.sidebarTagline}}</p>
        <div class="groups">
          <Groups />
        </div>
        <div class="users">
          <Users />
        </div>
      </section>
    </transition>
    <section class="messenger" :style="{ backgroundColor: visualSettings.bodyBackgroundColor }">
      <transition appear name="fade">
        <Messenger v-if="activeChannel" />
      </transition>
      <transition appear name="fade">
        <div v-if="!activeChannel && init" class="v-align">
          <div class="inner">
            <Notification 
              v-if="!channelsInit || groupChannels.length"
              message="Loading Channel..."
              :icon="['fas', 'spinner']" 
              type="info"
            />
            <Notification 
              v-if="channelsInit && !groupChannels.length"
              message="Please select a channel to start&nbsp;chatting"
              :icon="['fas', 'comments']" 
              type="info"
            />
          </div>
        </div>
      </transition>
      <div class="overlay" @click="closeSidebar()"></div>
    </section>
    <transition appear name="login">
      <Login v-if="showingLogin" />
    </transition>
    <transition appear name="sign-up">
      <SignUp v-if="showingSignUp" />
    </transition>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import Users from '@/components/Users';
import Groups from '@/components/Groups';
import Messenger from '@/components/Messenger';
import SignUp from '@/components/SignUp';
import Login from '@/components/Login';
import Notification from '@/components/Notification';

export default {
  name: 'Chat',
  data() {
    return {
      init: false,
      name: undefined,
      group: undefined
    }
  },
  components: {
    Groups,
    Users,
    Messenger,
    Notification,
    SignUp,
    Login
  },
  computed: {
    ...mapGetters([
      'groupChannels',
      'visualSettings'
    ]),
    ...mapState([
      'chatEnabled',
      'demoMode',
      'domainEnabled',
      'channelsInit',
      'showingSignUp',
      'showingLogin',
      'activeChannel',
      'channelGroup',
      'ballotWSConnected',
      'sidebarOpen'
    ])
  },
  watch: {
    ballotWSConnected(isConnected) {
      if(isConnected) {
        this.$socket.sendObj({
          action: 'join',
          groupName: `/user:${this.group}`
        });
      } else {
        this.$socket.sendObj({
          action: 'leave',
          groupName: `/user:${this.group}`
        });
      }
    }
  },
  methods: {
    closeSidebar() {
      this.sidebarOpen && this.setSidebarOpen(false);
    },
    ...mapMutations([
      'setSidebarOpen',
      'showSignUp',
      'setChatEnabled',
      'setDemoMode',
      'setExternalId',
      'initLocalStorageData',
      'setSignUpEmail'
    ]),
    ...mapActions([
      'loginUser',
      'setChannelGroup',
      'loadAccountFeatures',
      'loadSettings',
      'loadUsers',
      'loadChannels'
    ])
  },
  async mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    this.group = urlParams.get('group');
    this.name = urlParams.get('name');
    this.avatar = urlParams.get('avatar');
    this.email = urlParams.get('email');
    this.phone = urlParams.get('phone');
    this.bio = urlParams.get('bio');
    this.title = urlParams.get('title');
    this.facebookUrl = urlParams.get('facebookUrl');
    this.externalId = urlParams.get('externalId') || urlParams.get('userId') || urlParams.get('userID');
    this.demo = urlParams.get('demo');

    if (this.email) {
      this.setSignUpEmail(this.email);
    }

    this.initLocalStorageData();
    this.setChannelGroup(this.group);
    this.setExternalId(this.externalId);
    this.setDemoMode(this.demo);
    await this.loadAccountFeatures();
    if (!this.chatEnabled) {
      return this.$notify({
        group: 'app',
        type: 'error',
        title: 'Chat Disabled',
        text: 'Chat has not been enabled for this account. Please contact support.',
        duration: -1
      }); 
    }

    try {
      await Promise.all([
        this.loadSettings(),
        this.loadUsers(),
        this.loadChannels()
      ]);

      if (!this.domainEnabled) {
        return this.$notify({
          group: 'app',
          type: 'error',
          title: 'Domain Disabled',
          text: 'Chat has not been enabled for this domain. Please contact support.',
          duration: -1
        }); 
      }
    } catch (e) {
      this.$notify({
        group: 'app',
        type: 'error',
        title: 'Something Went Wrong',
        text: 'please contact support.',
        duration: -1
      });
    }

    await this.loginUser({
      name: this.name,
      avatar: this.avatar,
      email: this.email,
      bio: this.bio,
      phone: this.phone,
      title: this.title,
      facebookUrl: this.facebookUrl
    });

    setTimeout(() => {
      this.init = true;
    }, 250);
  }
}
</script>

<style scoped lang="scss">
#chat {
  position: relative;
  width: 100%;
  height: 100%;
  left: 0;

  @media only screen and (max-width: 768px) {
    transition: left 500ms cubic-bezier(0.230, 1.000, 0.320, 1.000);
  }

  .loading {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    z-index: 2;
  }

  .sidebar {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 250px;
    padding: 20px 15px;
    box-sizing: border-box;
    background: #eaebef;
    overflow: hidden;
    z-index: 2;
    display: flex;
    flex-flow: column;
    align-items: stretch;

    @media only screen and (max-width: 768px) {
      left: -100%;
      width: 100%;
    }

    img.logo {
      width: auto;
      max-width: 100%;
    }

    .tagline {
      font-size: 14.5px;
      line-height: 1.1;
      color: #fff;
      margin: 6px 0 20px;

      &:first-child {
        margin-top: -1px;
      }
    }

    .groups {
      flex: 0 1 auto;
    }

    .users {
      position: relative;
      flex: 1 1 auto;
    }
  }

  .messenger {
    position: absolute;
    left: 250px;
    top: 0;
    bottom: 0;
    right: 0;
    background: #fff;

    @media only screen and (max-width: 768px) {
      left: 0;
    }
  }

  .v-align {
    display: table;
    margin: 0 auto;
    height: 100%;
    
    > .inner {
      display: table-cell;
      vertical-align: middle;
    }
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    cursor: pointer;
    z-index: 2;
    display: none;
  }

  &.sidebarOpen {

    @media only screen and (max-width: 768px) {
      left: 100%;
    }

    .overlay {

      @media only screen and (max-width: 768px) {
        display: block;
      }
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: all 750ms cubic-bezier(0.230, 1.000, 0.320, 1.000);
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

::-webkit-scrollbar-thumb {
  border-color: #eaebef;
}
</style>
